// Daily Report Service - narrative CRUD via Azure Function
import { authService } from '../auth/authService';

export const EMPTY_DAILY_REPORT_NARRATIVE = {
  compiled_by: '',
  positive_safety_observation: '',
  incidents: '',
  safety_toolbox_topic: '',
  geology_meeting_topic: '',
};

export const EMPTY_SELECTED_IN_PROGRESS_HOLES = [];

class DailyReportService {
  constructor() {
    this.functionAppUrl =
      'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    this.apiUrl = `${this.functionAppUrl}/api/daily_reports`;
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken('qgisApi');
      const userInfo = authService.getUserInfo();

      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown',
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  normalizeNarrative(data) {
    if (!data || typeof data !== 'object') {
      return { ...EMPTY_DAILY_REPORT_NARRATIVE };
    }
    return {
      compiled_by: data.compiled_by ?? '',
      positive_safety_observation: data.positive_safety_observation ?? '',
      incidents: data.incidents ?? '',
      safety_toolbox_topic: data.safety_toolbox_topic ?? '',
      geology_meeting_topic: data.geology_meeting_topic ?? '',
    };
  }

  normalizeSelectedInProgressHoles(data) {
    const raw = data?.selected_in_progress_holes;
    if (!Array.isArray(raw)) return [...EMPTY_SELECTED_IN_PROGRESS_HOLES];
    return raw
      .filter((entry) => entry && typeof entry === 'object')
      .map((entry) => ({
        collar_uuid: entry.collar_uuid != null ? String(entry.collar_uuid) : null,
        hole_id: entry.hole_id != null ? String(entry.hole_id) : null,
      }))
      .filter((entry) => entry.collar_uuid || entry.hole_id);
  }

  async getDailyReport(reportDate) {
    if (!reportDate) {
      throw new Error('report_date is required');
    }

    const headers = await this.getAuthenticatedHeaders();
    const url = `${this.apiUrl}?report_date=${encodeURIComponent(reportDate)}`;

    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Failed to load daily report: ${response.status} ${text}`);
    }

    const result = await response.json();
    return {
      success: Boolean(result.success),
      data: result.data ? this.normalizeNarrative(result.data) : null,
      selectedInProgressHoles: this.normalizeSelectedInProgressHoles(result.data),
      raw: result.data ?? null,
    };
  }

  async upsertDailyReport(reportDate, payload = {}) {
    if (!reportDate) {
      throw new Error('report_date is required');
    }

    const headers = await this.getAuthenticatedHeaders();
    const narrative = this.normalizeNarrative(payload.narrative ?? payload);
    const selectedInProgressHoles = Array.isArray(payload.selectedInProgressHoles)
      ? payload.selectedInProgressHoles
      : this.normalizeSelectedInProgressHoles(payload);

    const body = {
      report_date: reportDate,
      ...narrative,
      selected_in_progress_holes: selectedInProgressHoles,
    };

    const response = await fetch(this.apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Failed to save daily report: ${response.status} ${text}`);
    }

    const result = await response.json();
    return {
      success: Boolean(result.success),
      data: this.normalizeNarrative(result.data),
      selectedInProgressHoles: this.normalizeSelectedInProgressHoles(result.data),
      message: result.message,
    };
  }
}

export const dailyReportService = new DailyReportService();
export default dailyReportService;
